import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Loader2 } from "lucide-react";

interface UrlCheckerFormProps {
  onCheck: (url: string) => void;
  isLoading: boolean;
  initialUrl?: string;
}

export function UrlCheckerForm({ onCheck, isLoading, initialUrl = "" }: UrlCheckerFormProps) {
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState("");

  const validateUrl = (input: string): boolean => {
    const trimmed = input.trim();
    
    if (!trimmed) {
      setError("Please enter a URL");
      return false;
    }

    // Add https:// if no protocol specified
    let urlToValidate = trimmed;
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
      urlToValidate = `https://${trimmed}`;
    }

    try {
      const parsed = new URL(urlToValidate);
      if (!parsed.hostname || !parsed.hostname.includes(".")) {
        setError("Please enter a valid domain (e.g., example.com)");
        return false;
      }
      setError("");
      return true;
    } catch {
      setError("Please enter a valid URL");
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateUrl(url)) {
      return;
    }

    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    onCheck(normalizedUrl);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-xl mx-auto">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Enter website URL (e.g., example.com)"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError("");
            }}
            className="h-12 text-base"
            disabled={isLoading}
          />
          {error && (
            <p className="text-sm text-risk mt-2">{error}</p>
          )}
        </div>
        <Button 
          type="submit" 
          size="lg" 
          className="h-12 px-6 btn-orange"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              Check Website
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
